import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChangeEvent, ReactNode } from 'react';
import { FiEdit } from 'react-icons/fi';
import { Button, InputField, SectionCard } from '@themes/aegix';
import type { ProfilePersonalData } from '../types';

/**
 * Args handed to a host-supplied phone-input renderer. AIM injects its E.164
 * `PhoneInputField` (react-phone-number-input) through this slot; SMS leaves it
 * undefined and gets the plain `<InputField type="tel">` default. Keeping the
 * dependency in the adapter is deliberate — the lib must not pull in
 * react-phone-number-input (SMS keeps the plain input).
 */
export interface RenderPhoneInputArgs {
  /** Current phone value (E.164 when the host formats it). */
  value: string;
  /** Update `formData.phone` with the next value. */
  onChange: (next: string) => void;
  /** True while a save is in flight. */
  disabled?: boolean;
  'data-e2e'?: string;
}

export interface ProfilePersonalSectionProps {
  value: ProfilePersonalData;
  /**
   * Persist edited details. May be async; if it rejects, the section stays in
   * edit mode (the host adapter surfaces its own error toast — the lib has no
   * toaster dependency).
   */
  onSave: (value: ProfilePersonalData) => void | Promise<void>;
  isSaving?: boolean;
  canEdit?: boolean;
  /**
   * Optional synchronous validator. Return a field→message map to block the
   * save and surface inline errors; return null / an empty map when valid.
   */
  validate?: (
    value: ProfilePersonalData,
  ) => Partial<Record<keyof ProfilePersonalData, string>> | null;
  /**
   * Optional override for the Mobile field's editor (EDIT mode only). When
   * provided, the section renders this instead of the default plain
   * `<InputField type="tel">` — AIM uses it to inject its E.164
   * `PhoneInputField` (validation/formatting). Omit it to keep the plain input
   * (SMS path, unchanged). The lib stays free of react-phone-number-input.
   */
  renderPhoneInput?: (args: RenderPhoneInputArgs) => ReactNode;
  title?: string;
  e2eTitle?: string;
}

type Errors = Partial<Record<keyof ProfilePersonalData, string>>;

/**
 * Personal details (name / email / phone) — read view with an inline edit
 * affordance. Presentational: validation + persistence come in via props, so
 * both apps share one editor despite their differing field names
 * (`cell_phone` vs `mobile`), which the adapter maps onto
 * {@link ProfilePersonalData}.
 */
export function ProfilePersonalSection({
  value,
  onSave,
  isSaving = false,
  canEdit = true,
  validate,
  renderPhoneInput,
  title,
  e2eTitle,
}: ProfilePersonalSectionProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProfilePersonalData>(value);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    setFormData(value);
  }, [value]);

  const handleChange =
    (field: keyof ProfilePersonalData) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  // Value-based setter for the injected phone input (it emits an E.164 string,
  // not a DOM change event like the plain InputField does).
  const handlePhoneChange = (next: string) => {
    setFormData((prev) => ({ ...prev, phone: next }));
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
  };

  const handleSave = async () => {
    const found = validate?.(formData) ?? null;
    if (found && Object.values(found).some(Boolean)) {
      setErrors(found);
      return;
    }
    try {
      await onSave(formData);
      setErrors({});
      setIsEditing(false);
    } catch {
      // Host adapter owns error messaging; keep the form open for a retry.
    }
  };

  const handleCancel = () => {
    setFormData(value);
    setErrors({});
    setIsEditing(false);
  };

  return (
    <SectionCard
      title={title ?? t('details', 'Details')}
      e2eTitle={e2eTitle}
      action={
        canEdit && !isEditing ? (
          <Box
            color="defaultSafety.primary"
            cursor="pointer"
            data-e2e="editUserDetail"
            onClick={() => setIsEditing(true)}
          >
            <FiEdit />
          </Box>
        ) : undefined
      }
    >
      {isEditing ? (
        <Flex flexDirection="column" gap={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <Box>
              <InputField
                placeholder={t('first_name', 'First Name')}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                data-e2e="firstname-input"
              />
              {errors.firstName && (
                <Text color="text.danger" fontSize="xs" mt={1}>
                  {errors.firstName}
                </Text>
              )}
            </Box>
            <Box>
              <InputField
                placeholder={t('last_name', 'Last Name')}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                data-e2e="lastname-input"
              />
              {errors.lastName && (
                <Text color="text.danger" fontSize="xs" mt={1}>
                  {errors.lastName}
                </Text>
              )}
            </Box>
          </SimpleGrid>

          <Box>
            <InputField
              placeholder={t('email', 'Email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              data-e2e="email-input"
            />
            {errors.email && (
              <Text color="text.danger" fontSize="xs" mt={1}>
                {errors.email}
              </Text>
            )}
          </Box>

          <Box>
            {renderPhoneInput ? (
              renderPhoneInput({
                value: formData.phone,
                onChange: handlePhoneChange,
                disabled: isSaving,
                'data-e2e': 'cellphone-input',
              })
            ) : (
              <InputField
                placeholder={t('mobile', 'Mobile')}
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange('phone')}
                data-e2e="cellphone-input"
              />
            )}
            {errors.phone && (
              <Text color="text.danger" fontSize="xs" mt={1}>
                {errors.phone}
              </Text>
            )}
          </Box>

          <SimpleGrid columns={2} gap={4} mt={2} w="100%">
            <Box height={10}>
              <Button
                visual="secondary"
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                w="100%"
                data-e2e="cancel-button"
              >
                {t('common.cancel', t('cancel', 'Cancel'))}
              </Button>
            </Box>
            <Box height={10}>
              <Button
                visual="primary"
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                w="100%"
                data-e2e="save-button"
              >
                {t('common.save', t('save', 'Save'))}
              </Button>
            </Box>
          </SimpleGrid>
        </Flex>
      ) : (
        <Flex flexDirection="column" alignItems="start" textAlign="start" gap={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} w="100%">
            <Box>
              <Text pb={1} color="text.secondary" fontSize="sm">
                {t('first_name', 'First Name')}
              </Text>
              <Text fontWeight={600}>{value.firstName || '-'}</Text>
            </Box>
            <Box>
              <Text pb={1} color="text.secondary" fontSize="sm">
                {t('last_name', 'Last Name')}
              </Text>
              <Text fontWeight={600}>{value.lastName || '-'}</Text>
            </Box>
          </SimpleGrid>

          <Box>
            <Text pb={1} color="text.secondary" fontSize="sm">
              {t('email', 'Email')}
            </Text>
            <Text fontWeight={600}>{value.email || '-'}</Text>
          </Box>

          <Box>
            <Text pb={1} color="text.secondary" fontSize="sm">
              {t('mobile', 'Mobile')}
            </Text>
            <Text fontWeight={600}>{value.phone || '-'}</Text>
          </Box>
        </Flex>
      )}
    </SectionCard>
  );
}

export default ProfilePersonalSection;
